import {
  DestroyRef,
  effect,
  EffectCleanupRegisterFn,
  inject,
  Injectable,
  signal,
  WritableSignal,
} from '@angular/core';
import { IGoalResponse } from 'app/features/goal/models/goal.models';
import { GoalApiService } from 'app/features/goal/data-access/goal-api.service';
import { Subscription } from 'rxjs';
import { GoalContext, GoalStore } from './goal-store';

type GoalDataQuery = GoalContext & {
  limit: number;
  page: number;
};

type GoalDataLoadOptions = {
  isBackgroundRefresh: boolean;
};

@Injectable()
export class GoalPageService {
  private readonly autoRefreshIntervalMs: number = 30000;
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly goalApi: GoalApiService = inject(GoalApiService);
  private readonly refreshTick: WritableSignal<number> = signal<number>(0);
  private readonly store: GoalStore = inject(GoalStore);
  private lastRefreshTick: number = 0;
  private loadedQueryKey: string | null = null;

  constructor() {
    this.startAutoRefresh();

    effect((onCleanup: EffectCleanupRegisterFn) => {
      const refreshTick: number = this.refreshTick();

      const context: GoalContext | null = this.store.context();

      if (!context) return;

      const page: number = this.store.activitiesPage();
      const queryKey: string = this.getQueryKey(context, page);
      const isBackgroundRefresh: boolean =
        this.loadedQueryKey === queryKey && this.lastRefreshTick !== refreshTick;

      this.lastRefreshTick = refreshTick;

      const subscription: Subscription = this.loadGoalData(
        {
          goal: context.goal,
          limit: this.store.expandedActivitiesPageSize,
          page,
          username: context.username,
        },
        { isBackgroundRefresh }
      );

      onCleanup(() => subscription.unsubscribe());
    });
  }

  setContext(username: string, goal: number): void {
    if (!username || !Number.isFinite(goal)) return;

    const context: GoalContext | null = this.store.context();

    if (context?.username === username && context.goal === goal) return;

    this.lastRefreshTick = this.refreshTick();
    this.loadedQueryKey = null;
    this.store.resetForContext({ goal, username });
  }

  setActivitiesPage(page: number): boolean {
    if (page < 0 || page === this.store.activitiesPage()) return false;

    this.store.activitiesPage.set(page);
    return true;
  }

  toggleActivitiesExpansion(): void {
    this.store.activitiesPage.set(0);
    this.store.areActivitiesExpanded.update((isExpanded: boolean) => !isExpanded);
  }

  refreshNow(): void {
    this.refreshTick.update((tick: number) => tick + 1);
  }

  private loadGoalData(query: GoalDataQuery, options: GoalDataLoadOptions): Subscription {
    if (!options.isBackgroundRefresh) {
      this.store.error.set(null);
      this.store.isLoading.set(true);
    }

    return this.goalApi
      .getGoalInformation({
        goal: query.goal,
        limit: query.limit,
        page: query.page,
        username: query.username,
      })
      .subscribe({
        error: (error: unknown) => {
          if (!options.isBackgroundRefresh) {
            this.loadedQueryKey = null;
            this.store.error.set(error);
            this.store.goalData.set(null);
          }

          this.store.isLoading.set(false);
        },
        next: (goalData: IGoalResponse) => {
          this.loadedQueryKey = this.getQueryKey(query, query.page);
          this.store.error.set(null);
          this.store.goalData.set(goalData);
          this.store.isLoading.set(false);
        },
      });
  }

  private startAutoRefresh(): void {
    const refreshIntervalId: number = window.setInterval(() => {
      this.refreshTick.update((tick: number) => tick + 1);
    }, this.autoRefreshIntervalMs);

    this.destroyRef.onDestroy(() => window.clearInterval(refreshIntervalId));
  }

  private getQueryKey(context: GoalContext, page: number): string {
    return `${context.username}:${context.goal}:${page}`;
  }
}
